import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { RoomFormDialog } from "@/components/admin/RoomFormDialog";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { EmptyState } from "@/components/brand/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { roomService, roomTypeService } from "@/lib/api";
import { useSelectedProperty } from "@/stores/property";
import type { Room, RoomCreate } from "@/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export function RoomsPage() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Room | null>(null);

  const propertyId = useSelectedProperty();

  const { data: rooms, isLoading } = useQuery({
    queryKey: ["rooms", propertyId],
    queryFn: () => roomService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const { data: roomTypes = [] } = useQuery({
    queryKey: ["room-types", propertyId],
    queryFn: () => roomTypeService.list(propertyId ?? ""),
    enabled: Boolean(propertyId),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["rooms"] });

  const createMutation = useMutation({
    mutationFn: (body: RoomCreate) => roomService.create(body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toast.success("호실이 등록되었습니다.");
    },
    onError: () => toast.error("등록에 실패했습니다."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Omit<RoomCreate, "property_id"> }) =>
      roomService.update(id, body),
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditing(null);
      toast.success("저장되었습니다.");
    },
    onError: () => toast.error("저장에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => roomService.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("삭제되었습니다.");
    },
    onError: (error) => {
      // 409: the room is still linked to one or more guests.
      const detail = isAxiosError(error) ? error.response?.data?.detail : null;
      toast.error(typeof detail === "string" ? detail : "삭제에 실패했습니다.");
    },
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (room: Room) => {
    setEditing(room);
    setDialogOpen(true);
  };

  const handleSubmit = (body: Omit<RoomCreate, "property_id">) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, body });
    } else {
      if (!propertyId) return;
      createMutation.mutate({ ...body, property_id: propertyId });
    }
  };

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium tracking-tight">호실</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            호실별 정보를 등록하면 게스트 응답에 객실 안내가 반영됩니다.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" /> 새 호실
        </Button>
      </div>

      <Card>
        {isLoading ? (
          <BrandLoader className="py-12" size={44} label="불러오는 중…" />
        ) : rooms && rooms.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>호실명</TableHead>
                <TableHead>위치</TableHead>
                <TableHead>정원</TableHead>
                <TableHead>편의시설</TableHead>
                <TableHead className="text-right">관리</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id}>
                  <TableCell className="font-medium">{room.name}</TableCell>
                  <TableCell>{room.floor ?? "-"}</TableCell>
                  <TableCell className="tabular-nums">
                    {room.max_guests != null ? `${room.max_guests}명` : "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {room.amenities.length > 0 ? room.amenities.join(", ") : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="수정"
                        onClick={() => openEdit(room)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="삭제"
                        onClick={() => deleteMutation.mutate(room.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            title="아직 등록된 호실이 없어요"
            description="호실을 등록하면 게스트를 호실에 연결하고 객실별 안내를 제공할 수 있어요."
            action={
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" /> 새 호실
              </Button>
            }
          />
        )}
      </Card>

      <RoomFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditing(null);
        }}
        room={editing}
        roomTypes={roomTypes}
        isPending={createMutation.isPending || updateMutation.isPending}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
